import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { Link, useNavigate, useSearchParams } from 'react-router'

import { Badge } from '@/components/ui/badge'
import { UserLabel } from '@/components/ui/user-label'
import { cn } from '@/lib/utils'
import { type SalesLeadListItem } from '@/models/salesLead'

function ScorePill({ score }: { score: number | null }) {
  if (score === null) return null
  const tone =
    score >= 8
      ? 'bg-emerald-100 text-emerald-700'
      : score >= 5
        ? 'bg-amber-100 text-amber-700'
        : 'bg-slate-100 text-slate-600'
  return <Badge className={cn('text-[10px]', tone)}>Score {score}</Badge>
}

export function PipelineCardContent({ lead }: { lead: SalesLeadListItem }) {
  return (
    <div className="bg-surface rounded-md border border-neutral-200 p-2.5 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            {lead.hasUnread && <span className="size-1.5 shrink-0 rounded-full bg-blue-500" />}
            <span className="truncate text-sm font-medium">{lead.legalName}</span>
          </div>
          <div className="text-muted-foreground mt-0.5 text-xs">
            DOT {lead.dotNumber}
            {lead.state ? ` · ${lead.state}` : ''}
          </div>
        </div>
        <ScorePill score={lead.conversionScore} />
      </div>
      <div className="text-muted-foreground mt-2 flex items-center justify-between text-[11px]">
        {lead.assignedRepName ? (
          <UserLabel
            name={lead.assignedRepName}
            src={lead.assignedRepAvatar}
            className="gap-1.5"
            avatarClassName="size-4"
            fallbackClassName="text-[8px]"
          />
        ) : (
          <span className="truncate">Unassigned</span>
        )}
        <span className="shrink-0" title="Days in current stage">
          {lead.daysInCurrentStage}d
        </span>
      </div>
    </div>
  )
}

export function DraggablePipelineCard({ lead }: { lead: SalesLeadListItem }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: lead.id,
    data: { status: lead.status, lead },
  })
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  // The link targets the full detail page (so the href is real for hover/right-
  // click). A plain left-click opens the board's right-side drawer; cmd/ctrl/
  // middle-click opens the detail in a new tab. We open the new tab explicitly
  // because dnd-kit's pointer listeners swallow the browser's native
  // modified/middle-click-to-new-tab behavior.
  const detailHref = `/insure/internal/sales-leads/${lead.id}`
  const drawerParams = new URLSearchParams(searchParams)
  drawerParams.set('lead', lead.id)
  const drawerTo = `/insure/internal/sales-leads/pipeline?${drawerParams.toString()}`

  function openDetailInNewTab() {
    window.open(detailHref, '_blank', 'noopener,noreferrer')
  }

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform) }}
      className={cn('touch-none', isDragging && 'opacity-40')}
      {...attributes}
      {...listeners}
    >
      {/* Stop drag listeners from swallowing the click-through to the drawer. */}
      <Link
        to={detailHref}
        onClick={(e) => {
          if (isDragging) {
            e.preventDefault()
            return
          }
          e.preventDefault()
          if (e.metaKey || e.ctrlKey || e.shiftKey) {
            openDetailInNewTab()
          } else {
            navigate(drawerTo)
          }
        }}
        onAuxClick={(e) => {
          // Middle-click → open the detail in a new tab.
          if (e.button === 1) {
            e.preventDefault()
            openDetailInNewTab()
          }
        }}
      >
        <PipelineCardContent lead={lead} />
      </Link>
    </div>
  )
}
